import Icon from 'shared/icons';
import React, { ChangeEventHandler, useEffect, useState } from 'react';
import CoreGrayButton from 'shared/button/CoreGrayButton';
import CoreSelect from 'shared/CoreSelect';
import CoreSvg from 'shared/CoreSvg';
import CoreButtonInput from 'shared/input/CoreButtonInput';
import CoreTag from 'shared/tag/CoreTag';
// import useManagementTable from 'src/hooks/management/useManagementTable';
// import { useGetInfluencerListQuery } from 'src/hooks/queries/management';
// import { useGetMembershipQuery } from 'src/hooks/queries/membership';
import useToast from 'hooks/useToast';
import styled from 'styled-components';
import useCommonStore from 'store/useCommonStore';
import { useGetFilterQuery, useGetMatchListQuery } from 'hooks/queries/match';
import { useNavigate } from 'react-router-dom';
import { useSetFilterMutation } from 'hooks/mutations/match';

const TableFooter = () => {
	// router
	const navigate = useNavigate();
	// toast
	const toast = useToast();
	// store
	const { page } = useCommonStore();

	const { data: filter_data = null } = useGetFilterQuery();
	const { data: { count = null, results: match_data = null } = {} } =
		useGetMatchListQuery();
	const { mutate: filterMutate } = useSetFilterMutation();

	/** 총 페이지 수를 구하는 함수 */
	const getTotalPageCount = (match_count: number) =>
		Math.ceil(match_count / +filter_data?.filter_info.page_size);

	/** 현재 페이지가 지금 보이는 페이지 리스트(길이 5)에서 몇 번째 위치인지 */
	const getPageLocationInPageList = (current_page: number) => Math.ceil(current_page / 5);

	// 총 페이지 수
	const [total_page, setTotalPage] = useState(
		filter_data && count ? getTotalPageCount(count) : 1,
	);
	// 현재 페이지의 페이지 리스트 내 위치 (5로 나누었을 때 나오는 페이지 리스트 중 몇 번째에 위치하고 있는지)
	const [current_page_list, setCurrentPageList] = useState(
		getPageLocationInPageList(page) ?? 1,
	);

	// 비동기로 인해 초기 값으로 state가 세팅되지 않았을 경우 다시 세팅
	useEffect(() => {
		if (match_data === null || filter_data === null) return;

		// setPageSizeUnit(`${filter_data?.filter_info.page_size}명`);
		setTotalPage(getTotalPageCount(count));
		setCurrentPageList(getPageLocationInPageList(page));
	}, [page, filter_data, count, match_data]);

	// 페이지 이동
	const movePage = (num: number) => {
		if (match_data === null || filter_data === null) return;

		if (num > total_page) {
			toast('존재하지 않는 페이지 요청입니다.', false, true);
			return;
		}

		navigate(`/imms/match?page=${num}`);
	};

	// 페이지 사이즈 변경
	const changeSize = (target: string) => {
		const size = parseInt(target, 10);
		filterMutate(
			{ ...filter_data?.filter_info, page_size: size.toString() },
			{
				onSuccess() {
					if (page !== 1) navigate('/imms/match?page=1');
				},
				onError() {
					toast('페이지 사이즈 변경에 실패했습니다.', false, true);
				},
			},
		);
	};

	// 페이지 이동 input에 입력된 값
	const [inputted_page, setInputtedPage] = useState('');
	// input에 숫자만 입력할 수 있도록하는 onChange
	const onChangeInputtedPage: ChangeEventHandler<HTMLInputElement> = (e) => {
		const value = parseInt(e.currentTarget.value, 10);
		setInputtedPage(value.toString());
	};

	return (
		<TableFooterWrapper>
			<CoreSelect
				select_type="box"
				option_list={['5명', '10명', '25명', '50명']}
				selected_value={filter_data?.filter_info.page_size + '명/page'}
				selected_option={filter_data?.filter_info.page_size}
				setSelectedOption={changeSize}
				_width="240px"
			/>

			<TablePageButtonGroup>
				{/* 페이지 리스트 왼쪽으로 이동하는 버튼 */}
				{current_page_list > 1 && (
					<CoreSvg _onClick={() => setCurrentPageList((state) => state - 1)}>
						<Icon.ArrowLeft />
					</CoreSvg>
				)}
				{/* 페이지 리스트가 2번째 이상일 때 1페이지 버튼과 버튼그룹 사이의 말 줄임 */}
				{current_page_list > 1 && total_page > 5 && (
					<>
						<CoreTag
							tag_type="num"
							num={1}
							color_type="gray"
							_onClick={() => movePage(1)}
						/>
						<span>...</span>
					</>
				)}
				{/* 페이지 버튼 그룹 - 메인 5개 */}
				{Array(
					total_page - current_page_list * 5 >= 0
						? 5
						: total_page - (current_page_list - 1) * 5 > 0
						? total_page - (current_page_list - 1) * 5
						: 5,
				)
					.fill(null)
					.map((item, idx) => {
						const current_num = idx + 1 + (current_page_list - 1) * 5;
						return (
							<CoreTag
								key={current_num}
								tag_type="num"
								num={current_num}
								color_type={current_num === page ? 'primary' : 'gray'}
								_onClick={() => movePage(current_num)}
							/>
						);
					})}
				{/* 페이지 리스트가 1번째 일 때 마지막 페이지 버튼과 버튼그룹 사이의 말 줄임 */}
				{current_page_list === 1 && total_page > 5 && (
					<>
						<span>...</span>
						<CoreTag
							tag_type="num"
							num={total_page}
							color_type="gray"
							_onClick={() => movePage(total_page)}
						/>
					</>
				)}
				{/* 페이지 리스트 오른쪽으로 이동하는 버튼 */}
				{getPageLocationInPageList(total_page) > current_page_list && (
					<CoreSvg _onClick={() => setCurrentPageList((state) => state + 1)}>
						<Icon.ArrowRight />
					</CoreSvg>
				)}
			</TablePageButtonGroup>
			<CoreButtonInput
				input_id="page-number-input"
				_width="240px"
				_placeholder="25"
				_value={inputted_page}
				_onChange={onChangeInputtedPage}
			>
				<CoreGrayButton
					size="small"
					style_type="tonal"
					text="이동"
					_onClick={() => movePage(+inputted_page)}
				/>
			</CoreButtonInput>
		</TableFooterWrapper>
	);
};

const TableFooterWrapper = styled.div`
	height: 136px;
	padding: 32px 32px 48px;
	background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
	border-radius: 0 0 16px 16px;

	${({ theme }) => theme.common.flexBetween}

	position: sticky;
	left: 0px;
`;

const TablePageButtonGroup = styled.div`
	${({ theme }) => theme.common.flexBetween};

	& > span:not(:last-child) {
		${({ theme }) => theme.typo.body.p_bold};
		margin-right: 16px;
	}

	& > div:not(:last-child) {
		margin-right: 16px;
	}
`;

export default TableFooter;
